import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { useUnsavedGuard, DiscardDialog } from "@/components/useUnsavedGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const RECENT_KEY = "tdops_recent_punt_locs";

export function getRecentLocations() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
}
export function pushRecentLocation(loc) {
  if (!loc?.location_id) return;
  const cur = getRecentLocations().filter((l) => l.location_id !== loc.location_id);
  localStorage.setItem(RECENT_KEY, JSON.stringify([{ location_id: loc.location_id, name: loc.name }, ...cur].slice(0, 3)));
}

/**
 * PuntQuickDialog — capture a loose action item in seconds, one-handed.
 * One autofocused title field, Enter saves; a row of location chips (last 3 +
 * a select); everything else hidden behind "Meer". A punt never gets a date.
 */
export const PuntQuickDialog = ({ open, onOpenChange, presetLocationId, presetLocationName, onSaved }) => {
  const [title, setTitle] = useState("");
  const [locationId, setLocationId] = useState(null);
  const [description, setDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState("__none__");
  const [assetId, setAssetId] = useState("__none__");
  const [showMore, setShowMore] = useState(false);
  const [locations, setLocations] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const isDirty = title.trim() !== "" || (showMore && (description.trim() !== "" || assignedTo !== "__none__" || assetId !== "__none__"));
  const guard = useUnsavedGuard(isDirty, () => onOpenChange(false));

  useEffect(() => {
    if (!open) return;
    // Reset per open.
    setTitle(""); setDescription(""); setAssignedTo("__none__"); setAssetId("__none__"); setShowMore(false);
    setLocationId(presetLocationId || null);
    (async () => {
      try {
        const [loc, asg] = await Promise.all([api.get("/locations"), api.get("/users/assignees")]);
        setLocations(loc.data); setAssignees(asg.data);
      } catch (e) { /* refs optional */ }
    })();
    setTimeout(() => inputRef.current?.focus(), 80);
  }, [open, presetLocationId]);

  useEffect(() => {
    if (!locationId) { setAssets([]); return; }
    (async () => {
      try { const { data } = await api.get(`/assets?location_id=${locationId}`); setAssets(data); }
      catch (e) { setAssets([]); }
    })();
  }, [locationId]);

  // Chips: recent locations, plus the preset one if it isn't already there.
  const recent = getRecentLocations();
  const chipLocs = [...recent];
  if (presetLocationId && !chipLocs.some((l) => l.location_id === presetLocationId)) {
    const nm = locations.find((l) => l.location_id === presetLocationId)?.name || presetLocationName || presetLocationId;
    chipLocs.unshift({ location_id: presetLocationId, name: nm });
  }
  const otherLocs = locations.filter((l) => !chipLocs.some((c) => c.location_id === l.location_id));
  const selectedName = locations.find((l) => l.location_id === locationId)?.name
    || chipLocs.find((l) => l.location_id === locationId)?.name;

  const save = async () => {
    if (!title.trim()) { toast.error("Geef het punt een titel."); inputRef.current?.focus(); return; }
    setSaving(true);
    try {
      await api.post("/punten", {
        title: title.trim(),
        description: showMore ? description : "",
        location_id: locationId,
        assigned_to: assignedTo === "__none__" ? null : assignedTo,
        asset_id: assetId === "__none__" ? null : assetId,
      });
      if (locationId && selectedName) pushRecentLocation({ location_id: locationId, name: selectedName });
      toast.success("Punt genoteerd");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(getErrorMessage(e, "Opslaan mislukt."));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={guard.requestClose}>
      <DialogContent className="max-w-md" data-testid="punt-quick-dialog" srDescription="Noteer snel een punt. Je kunt het later inplannen of omzetten naar een storing.">
        <DialogHeader><DialogTitle>Nieuw punt</DialogTitle></DialogHeader>
        <DiscardDialog guard={guard} />
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="punt-title">Wat moet er gebeuren?</Label>
            <Input
              id="punt-title"
              ref={inputRef}
              data-testid="punt-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }}
              placeholder="Bijv. deur klemt, leverancier terugbellen"
              autoComplete="off"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-data text-muted-foreground">Locatie (optioneel)</Label>
            <div className="flex flex-wrap items-center gap-2">
              {chipLocs.map((l) => (
                <button
                  key={l.location_id}
                  type="button"
                  data-testid={`punt-loc-chip-${l.location_id}`}
                  onClick={() => setLocationId(locationId === l.location_id ? null : l.location_id)}
                  className={`rounded border px-3 py-1 text-data transition-colors duration-150 ${
                    locationId === l.location_id
                      ? "border-inkt-zacht bg-inkt-zacht text-white"
                      : "border-border bg-background text-foreground hover:bg-muted"
                  }`}
                >
                  {l.name}
                </button>
              ))}
              <Select
                value={otherLocs.some((l) => l.location_id === locationId) ? locationId : ""}
                onValueChange={(v) => setLocationId(v)}
              >
                <SelectTrigger className="h-8 w-40 text-data" data-testid="punt-loc-select"><SelectValue placeholder="Andere locatie…" /></SelectTrigger>
                <SelectContent>
                  {otherLocs.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {!showMore ? (
            <button type="button" className="text-data text-primary hover:underline" data-testid="punt-more-toggle" onClick={() => setShowMore(true)}>
              Meer (omschrijving, toewijzen, asset)
            </button>
          ) : (
            <div className="space-y-3 rounded-md border border-border p-3">
              <div className="space-y-1.5"><Label className="text-data">Omschrijving</Label><Textarea rows={2} data-testid="punt-description-input" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
              <div className="space-y-1.5">
                <Label className="text-data">Toewijzen aan</Label>
                <Select value={assignedTo} onValueChange={setAssignedTo}>
                  <SelectTrigger className="h-8"><SelectValue placeholder="Niemand" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Niemand</SelectItem>{assignees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {locationId && (
                <div className="space-y-1.5">
                  <Label className="text-data">Asset</Label>
                  <Select value={assetId} onValueChange={setAssetId}>
                    <SelectTrigger className="h-8"><SelectValue placeholder="Geen" /></SelectTrigger>
                    <SelectContent><SelectItem value="__none__">Geen</SelectItem>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.asset_code} — {a.brand} {a.model}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          <Button className="w-full" data-testid="punt-save-button" onClick={save} disabled={saving}>
            {saving ? "Bezig..." : "Punt opslaan"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
